import * as React from 'react';
import { Info, Zap } from 'lucide-react';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Quote,
  TriCheckbox,
  WorkspaceButton,
  WorkspaceSelect,
  linkishClass,
} from '@/components/ui/workspace';
import type { Criticality } from '@/lib/api/engagement-requirements';
import type { Suggestion } from '@/lib/api/discovery-suggestions';
import { cx } from '@/lib/utils/cn';
import { EvidenceCite, nameBesideCitation } from '@/components/discovery/findings/evidence-bits';
import { CHOOSABLE_CRITICALITIES, CRITICALITY_LABEL, asCriticality } from '../requirements-logic';
import { isPicked, landsInText, matchText, onePersonSoFar, supportText } from './suggestions-logic';

/**
 * One requirement suggestion (`.cand.sug` in the discovery prototype): on
 * the left what it is and the evidence in their words; on the right what
 * happens when it is added (criticality, where it lands) and Dismiss.
 * Other library matches open in place (Decision 54.35).
 */

const LABEL = 'text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500';

export function SuggestionCard({
  s,
  busy,
  onPick,
  onCriticality,
  onSwitch,
  onDismiss,
}: {
  s: Suggestion;
  busy: boolean;
  onPick: (picked: boolean) => void;
  onCriticality: (c: Criticality) => void;
  onSwitch: (libraryItemId: string) => void;
  onDismiss: () => void;
}) {
  const [altsOpen, setAltsOpen] = React.useState(false);
  const picked = isPicked(s);
  const name = s.library_item?.number ?? (s.text.length > 60 ? `${s.text.slice(0, 57)}…` : s.text);
  const match = matchText(s);
  const criticality = asCriticality(s.criticality);

  return (
    <article
      aria-label={s.library_item ? `${s.library_item.number}: ${s.text}` : s.text}
      className={cx(
        'grid grid-cols-[minmax(0,1fr)_280px] gap-5 rounded-card border border-l-4 border-ink-100 bg-paper-0 px-4 py-3.5 max-[1100px]:grid-cols-1',
        picked ? 'border-l-posture-healthy' : 'border-l-ai-surface-border',
      )}
    >
      <div className="flex min-w-0 flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <TriCheckbox
            state={picked ? 'on' : 'off'}
            aria-label={`Select ${name}`}
            onChange={(next) => onPick(next)}
          />
          {s.library_item ? (
            <span className="font-mono text-body-sm font-medium text-ink-900">
              {s.library_item.number}
            </span>
          ) : (
            <StatusChip tone="neutral">New · numbered when added</StatusChip>
          )}
          <span className="text-meta text-ink-500">{s.module.name}</span>
          {s.match ? (
            <StatusChip tone="healthy">
              {s.match.status === 'removed'
                ? 'Removed from requirements'
                : 'Already in requirements'}
            </StatusChip>
          ) : null}
          {onePersonSoFar(s) ? <StatusChip tone="at-risk">One person so far</StatusChip> : null}
          <span className="flex-1" />
          <AiConfidenceChip value={s.confidence} />
        </div>
        <p className="text-body text-ink-900">{s.text}</p>
        {s.alternatives.length ? (
          <div>
            <button
              type="button"
              className={linkishClass()}
              aria-expanded={altsOpen}
              onClick={() => setAltsOpen((v) => !v)}
            >
              {altsOpen ? 'Hide other matches' : `Other library matches (${s.alternatives.length})`}
            </button>
            {altsOpen ? (
              <div className="mt-1.5 flex flex-col gap-2">
                {s.alternatives.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-start gap-2.5 rounded-chip border border-ink-100 bg-paper-50 px-2.5 py-2"
                  >
                    <span className="font-mono text-meta font-medium text-ink-700">{a.number}</span>
                    <span className="flex-1 text-body-sm text-ink-900">{a.text}</span>
                    <WorkspaceButton disabled={busy} onClick={() => onSwitch(a.id)}>
                      Use this instead
                    </WorkspaceButton>
                  </div>
                ))}
                <p className="text-meta text-ink-500">
                  AI found these as other possible fits. The evidence stays the same; only the
                  library requirement changes.
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
        {s.near.map((n) => (
          <p key={n.number} className="flex items-start gap-1 text-meta text-ink-500">
            <Info className="mt-[3px] h-3 w-3 flex-none" aria-hidden="true" />
            <span>
              Close to {n.number} in the library: {n.note}
            </span>
          </p>
        ))}
        <p className={`${LABEL} mt-0.5`}>In their words</p>
        {s.evidence.map((e) => {
          const gone = e.status === 'withdrawn';
          return (
            <div
              key={e.evidence_id}
              className="rounded-chip border border-ink-100 bg-paper-50 px-2.5 py-2"
            >
              <Quote size="sm" struck={gone}>
                {e.text}
              </Quote>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {e.number ? (
                  <span className="font-mono text-meta font-medium text-ink-700">{e.number}</span>
                ) : null}
                <EvidenceCite citation={e.citation} withdrawn={gone} />
                {nameBesideCitation(e.citation, e.source) ? (
                  <span className="text-meta text-ink-500">
                    {nameBesideCitation(e.citation, e.source)}
                  </span>
                ) : null}
                {gone ? (
                  <StatusChip tone="frozen" size="sm">
                    Withdrawn
                  </StatusChip>
                ) : null}
              </div>
            </div>
          );
        })}
        <p className="text-meta text-ink-500">{supportText(s)}</p>
      </div>

      <div className="flex flex-col gap-3">
        {match ? (
          <p className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-meta text-ink-700">
            {match}
          </p>
        ) : (
          <div>
            <label className={`${LABEL} mb-1.5 block`} htmlFor={`crit-${s.id}`}>
              Criticality
            </label>
            <WorkspaceSelect
              id={`crit-${s.id}`}
              className="h-8 text-body-sm"
              value={criticality}
              disabled={busy}
              onChange={(e) => onCriticality(asCriticality(e.target.value))}
            >
              {criticality === 'not_set' ? (
                <option value="not_set">{CRITICALITY_LABEL.not_set}</option>
              ) : null}
              {CHOOSABLE_CRITICALITIES.map((c) => (
                <option key={c} value={c}>
                  {CRITICALITY_LABEL[c]}
                </option>
              ))}
            </WorkspaceSelect>
            {s.suggested_criticality ? (
              <p className="mt-1 flex items-start gap-1 text-meta text-ink-500">
                <Zap className="mt-[3px] h-3 w-3 flex-none" aria-hidden="true" />
                <span>Suggested from what people said. Change it freely.</span>
              </p>
            ) : null}
          </div>
        )}
        <div>
          <p className={`${LABEL} mb-1`}>Lands in</p>
          <p className="text-body-sm text-ink-700">{landsInText(s)}</p>
        </div>
        <div>
          <button type="button" className={linkishClass()} onClick={onDismiss}>
            Dismiss…
          </button>
        </div>
      </div>
    </article>
  );
}
